/**
 * Site Login Page
 * Simple password protection gate for the entire application
 */

import { useState, useCallback } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Space } from 'antd';
import { LockOutlined, LoginOutlined, MailOutlined } from '@ant-design/icons';
import { useSiteAuth } from '@/stores/useSiteAuthStore';
import { usePreferencesStore } from '@/stores/usePreferencesStore';

const { Title, Text } = Typography;

interface LoginFormValues {
  email: string;
  password: string;
}

export function SiteLoginPage() {
  const [form] = Form.useForm<LoginFormValues>();
  const { login, devBypass, isLoading, error, clearError } = useSiteAuth();
  const { theme } = usePreferencesStore();
  const isDark = theme === 'dark';
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (values: LoginFormValues) => {
      clearError();
      setSubmitting(true);

      try {
        const success = await login(values.email, values.password);
        if (!success) {
          // Keep form open, error will be shown
          form.setFields([
            {
              name: 'password',
              errors: ['Invalid email or password'],
            },
          ]);
        }
        // If successful, the parent component will redirect
      } finally {
        setSubmitting(false);
      }
    },
    [login, clearError, form]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        form.submit();
      }
    },
    [form]
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark
          ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          borderRadius: 12,
          background: isDark ? '#1f1f1f' : '#fff',
        }}
        styles={{
          body: {
            padding: 32,
          },
        }}
      >
        <Space
          direction="vertical"
          size="large"
          style={{ width: '100%', textAlign: 'center' }}
        >
          <div>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: isDark
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <LockOutlined style={{ fontSize: 28, color: '#fff' }} />
            </div>
            <Title level={3} style={{ marginBottom: 8 }}>
              Maps Scraper
            </Title>
            <Text type="secondary">Sign in with your account</Text>
          </div>

          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              closable
              onClose={clearError}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            style={{ width: '100%', textAlign: 'left' }}
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input
                size="large"
                placeholder="Email"
                prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password
                size="large"
                placeholder="Password"
                prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                onKeyDown={handleKeyDown}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={isLoading || submitting}
                icon={<LoginOutlined />}
              >
                {isLoading || submitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </Form.Item>
          </Form>

          {import.meta.env.DEV && (
            <Button
              type="dashed"
              size="small"
              block
              onClick={devBypass}
              style={{ opacity: 0.6 }}
            >
              Dev Bypass
            </Button>
          )}
        </Space>
      </Card>
    </div>
  );
}

export default SiteLoginPage;
